import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle, Trash2 } from 'lucide-react'
import type { RequestApproval } from '../api'

interface RequestApprovalDeleteDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    requestApproval: RequestApproval | null
    onConfirm: () => void
    isLoading?: boolean
}

export function RequestApprovalDeleteDialog({
    open,
    onOpenChange,
    requestApproval,
    onConfirm,
    isLoading = false,
}: RequestApprovalDeleteDialogProps) {
    const { t } = useTranslation()
    if (!requestApproval) return null

    const objectID = requestApproval.objectID || ''
    const objectDescription = requestApproval.objectDescription || ''

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Trash2 className="h-5 w-5 text-destructive" />
                        {t('requestApprovals.deleteTitle')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('requestApprovals.confirmDelete', { 
                            objectID, 
                            objectDescription 
                        })}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            {t('requestApprovals.deleteWarning')}
                        </AlertDescription>
                    </Alert>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isLoading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={onConfirm}
                        disabled={isLoading}
                    >
                        {isLoading ? t('requestApprovals.deleting') : t('requestApprovals.delete')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
